/**
 * replaceEverywhereExecutor — `replace_text` en mode partout (`everywhere:true`) :
 * une liste de couples ancien / nouveau, chacun remplacé à chaque occurrence du
 * corps et de ses tableaux, par UN plan atomique de l'éditeur.
 *
 * WHY ce mode : « passe 1 500 € à 2 300 € partout » coûtait une lecture ciblée
 * puis un `apply_edit_plan` d'une étape par occurrence, que l'agent composait à
 * la main — en oubliant le dispositif, ou en touchant « 21 500 € ». Ici le
 * navigateur cherche, vérifie, puis écrit ; l'agent ne dit que les textes et
 * combien de fois il les a lus.
 *
 * ## Les règles du mode
 *
 * - **Le navigateur cherche au fichier, pas dans l'éditeur.** Les occurrences
 *   sont trouvées sur le fichier Word exporté (`relireLeFichierDeLActe`, puis
 *   `corpsAvecSesCellules`), clôturé sur la révision attendue : la seule lecture
 *   qui rende le texte que l'avocat voit, cellules de tableau comprises. Ce qui
 *   est cherché, compté et refusé se décide dans `remplacementPartout.ts` (pur),
 *   dont l'en-tête dit les deux garde-fous : mot entier, compte attendu.
 * - **Tout ou rien.** Un compte qui diffère, une occurrence hors de portée, une
 *   étape que l'aperçu ne prouve pas : RIEN n'est écrit pour tout l'appel, et
 *   le reçu (`replaceEverywhere.pairs`) rend les emplacements. L'écriture est UN
 *   `mutations.apply` d'un plan `atomic:true` — jamais une boucle, jamais deux
 *   plans : la somme des `expectedCount` est bornée par le contrat à 50
 *   (`CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX`), la taille d'un plan
 *   d'`apply_edit_plan` — le seul plan de `text.rewrite` que la production
 *   envoie.
 * - **Le régime du document.** Le plan part dans le mode de révision du
 *   document (`modeDeRevision`), comme `apply_edit_plan` : suivi activé, chaque
 *   remplacement est une révision ; sinon l'écriture est directe.
 * - **Toujours sensible à la casse**, sans `scope`, sans remplacement vide,
 *   sans espace au bord d'un texte : le contrat le refuse avant l'envoi
 *   (`actionCatalog.ts`).
 * - **Portée : le corps et ses tableaux.** En-têtes, pieds de page et notes de
 *   bas de page ne sont ni cherchés ni écrits (l'éditeur y refuse les plans,
 *   `horsDuCorps.ts`) ; le reçu le dit à chaque appel.
 * - **Deux exports par appel**, jamais un par occurrence : le premier trouve,
 *   le second relit ce qui est écrit (0,4 à 1,2 s chacun sur un acte de 1,5 Mo,
 *   en-tête d'`exportDeLActe.ts`).
 *
 * ## ⚠️ Pas de garde de portée d'inspection par bloc — exception décidée
 *
 * Les verbes de CONTENU refusent une cible que la dernière lecture n'a pas
 * publiée (`inspectedPublicNodeIds`). Ce mode ne la consulte pas, par décision
 * du porteur (2026-10-08) : il peut écrire dans un bloc que la lecture n'a pas
 * publié individuellement — au-delà des 250 repères d'une lecture globale, et
 * dans une cellule de tableau le jour où elles s'ouvrent. La raison : la garde
 * protège contre un texte que l'agent n'a pas lu, et ici le COMPTE ATTENDU en
 * tient lieu — l'agent affirme combien de fois il a lu chaque texte, le
 * navigateur le vérifie sur tout l'acte, et un écart refuse tout. Les
 * identifiants viennent du fichier, pas du modèle, et l'aperçu puis la
 * relecture les confrontent à l'éditeur. Sans cette exception le mode serait
 * inutilisable sur un long acte. Le courtier backend exige toujours une lecture
 * finale qui arme une portée avant tout `replace_text` (`CanvasSessionBroker`).
 * ⚠️ Ne pas étendre l'exception à un verbe qui change du texte sans un compte
 * vérifié de cette sorte.
 *
 * ## Ce qui est mesuré, et ce qui ne l'est pas
 *
 * RIEN de ce mode n'a tourné dans un éditeur réel à l'écriture de ce fichier.
 * Il n'emprunte donc que ce qu'`apply_edit_plan` fait déjà en production
 * (`editPlanExecutor.ts`) : des étapes `text.rewrite` adressées par un texte
 * exact, trouvé une fois dans un paragraphe du corps, sous aperçu, puis UNE
 * application. La sonde `tests/ws-mocked/mesure-replace-text-partout.spec.ts`
 * (R1 à R12, à exécuter ; relevé à consigner au §20 de `superdoc-document-api.md`)
 * doit établir le reste, que le code REFUSE en attendant :
 *
 * - un même texte plusieurs fois dans un paragraphe — étape par position (R2),
 *   ordre des réécritures dans un paragraphe (R3) : refusé, `not_unique_in_block` ;
 * - un paragraphe de cellule visé par son identifiant (R5) : refusé, `table_cell` ;
 * - le point d'extension des deux est `raisonHorsDePortee` (`remplacementPartout.ts`).
 *
 * Non mesuré et NON refusé :
 *
 * - plusieurs `text.rewrite` de textes DIFFÉRENTS dans un même paragraphe — le
 *   chemin de production le porte pour un plan de retouches ; ils sont gardés
 *   uniques à chaque état intermédiaire (`unicitesDansLeParagraphe`) ;
 * - ⚠️ l'adresse d'une étape : `within.nodeType` vaut `'paragraph'` en dur, avec
 *   l'identifiant du FICHIER, pour tout bloc — titre et élément de liste
 *   compris. En production, cette adresse n'existe que pour `format.apply`
 *   (`planDeFormatText.ts`, où l'éditeur est mesuré ne pas décider du bloc sur
 *   `nodeType`) ; `apply_edit_plan` envoie, lui, le type que l'inspection a
 *   publié. Pour un `text.rewrite` sur un titre ou un élément de liste, rien
 *   n'est mesuré (R8). Si l'éditeur refuse, c'est à l'aperçu, et l'appel est
 *   refusé en nommant le bloc (`REPLACE_EVERYWHERE_OUT_OF_REACH`) ;
 * - le coût d'un plan de 40 étapes, et de 200 (plafond du moteur) : R7, dont
 *   dépendent la borne backend (`SuperDocCanvasTools.ts`) et un relèvement de
 *   la borne de 50.
 *
 * Non dit par le reçu faute de mesure : le nombre d'entrées d'historique que
 * laisse le plan (R1, R6) — le reçu ne promet donc aucun compte d'annulation.
 *
 * ## Ce que l'aperçu doit prouver
 *
 * Ce qu'`apply_edit_plan` exige : un aperçu lisible, sur la révision attendue,
 * valide, qui rend les étapes du plan dans l'ordre. En plus, tout ce qu'il dit
 * d'une étape est confronté : un compte (`matchCount`) différent de 1, ou une
 * résolution qui n'est pas unique, dans le bon bloc, sur le texte du document,
 * fait refuser l'appel en nommant le bloc. R1 dira si l'aperçu rend TOUJOURS
 * ces résolutions pour un `text.rewrite` : l'exigence pourra alors devenir
 * ferme. L'aperçu ne voit pas tout (un texte qui traverse un champ Word vide
 * fait JETER l'application, `champsVides.ts`) : la révision inchangée prouve
 * alors que rien n'est écrit.
 *
 * ## Après l'écriture
 *
 * Le reçu de l'éditeur doit rendre chaque étape `changed`, trouvée une fois,
 * entre les révisions lues avant et après ; puis le fichier est RELU, clôturé
 * sur la révision d'après, et le texte de chaque paragraphe réécrit comparé à
 * celui attendu (`textesAttendus`). Un fichier qui ne se relit pas — raison
 * rendue, ou erreur levée par la relecture — est dit par le contrôle ET par la
 * description de l'effet, sans défaire la preuve de l'éditeur : l'écriture a eu
 * lieu, la rejouer la doublerait.
 */

import type { CanvasActionInput, CanvasReplaceEverywhereReceipt } from '@hector/shared-types';

import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  inconclusiveAfterMutation,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import { lirePreview, lireRecuDePlan, resolutionsParEtape } from './apercuDePlan';
import { idsDeRevision } from './canvasSnapshot';
import { REFUS_TEXTE_DANS_UN_CHAMP } from './champsVides';
import { relireLActeApresLEcriture, relireLeFichierDeLActe } from './ecritureDeForme';
import { estArretDeLot } from './executionAsynchrone';
import { ExportDeLActeImpossible } from './exportDeLActe';
import { muterSousFences, REVISION_ABSENTE, revisionCourante } from './fencesDeMutation';
import { corpsAvecSesCellules, LectureDeFormeImpossible } from '@hector/docx-forme';
import { texteDuParagraphe } from './planDeFormatText';
import {
  etapesDeRemplacement,
  occurrencesARemplacer,
  PORTEE_DITE,
  refusHorsDePortee,
  textesAttendus,
  verdictDesComptes,
  type CoupleARemplacer,
  type EtapeEtSonOccurrence,
  type OccurrenceARemplacer,
} from './remplacementPartout';
import { modeDeRevision, snapshot, type CanvasActionContext } from './textActionExecutors';

type ReplaceTextInput = Extract<CanvasActionInput, { action: 'replace_text' }>;

const VERBE = 'replace_text everywhere';

/** Nombre maximal d'étapes ou de blocs nommés dans un contrôle du reçu. Valeur de jugement. */
const NOMMES_AU_PLUS = 10;

/** Les couples de l'appel, ou `null` si un edit n'a pas son compte attendu — ce que le contrat refuse avant l'envoi. */
function couplesDeLAppel(input: ReplaceTextInput): CoupleARemplacer[] | null {
  const couples = input.edits.flatMap(({ find, replace, expectedCount }): CoupleARemplacer[] => (expectedCount === undefined ? [] : [{ find, replace, expectedCount }]));
  return couples.length === input.edits.length ? couples : null;
}

/** La cause qu'un aperçu donne du refus d'une étape, bornée : elle part dans le reçu que lit le modèle. */
const causeDeLEditeur = (code: string, message: string | undefined): string => (
  `the editor refuses it at preview (${code}${message === undefined ? '' : `: ${message.slice(0, 200)}`})`
);

/**
 * Les étapes que l'aperçu ne prouve pas, avec ce qu'il en dit. Vide : chaque
 * étape est trouvée une fois, et tout ce que l'aperçu en rend est cohérent
 * avec le fichier (voir l'en-tête, « Ce que l'aperçu doit prouver »).
 *
 * @param apercu La réponse brute d'un aperçu VALIDE du plan.
 * @param comptes Le compte que l'aperçu donne par étape, quand il en donne un.
 */
function etapesNonProuvees(
  apercu: unknown,
  comptes: ReadonlyMap<string, number | undefined>,
  etapes: readonly EtapeEtSonOccurrence[],
): Array<{ occurrence: OccurrenceARemplacer; cause: string }> {
  const resolutions = resolutionsParEtape(apercu);
  return etapes.flatMap(({ etape, occurrence }) => {
    const compte = comptes.get(etape.id);
    if (compte !== undefined && compte !== 1) return [{ occurrence, cause: `the editor finds its text ${compte} time(s) there, expected exactly 1` }];
    const resolues = resolutions.get(etape.id);
    if (resolues === undefined) return [];
    const [seule, ...autres] = resolues;
    const prouvee = seule !== undefined && autres.length === 0 && seule.blockId === occurrence.nodeId && seule.texte === occurrence.texte;
    return prouvee ? [] : [{ occurrence, cause: 'the editor does not resolve its text to one passage of that block reading as the document file does' }];
  });
}

/**
 * Exécute un `replace_text` en mode partout.
 *
 * Tout refus antérieur à `mutations.apply` est un reçu `aborted` : rien n'est
 * écrit. Une fois l'application soumise, toute réponse anormale de l'éditeur
 * est non rejouable (`non_verifiable`) : l'effet a pu se poser.
 *
 * @param input Un `replace_text` dont `everywhere` vaut `true` (`estUnRemplacementPartout`).
 */
export async function executeReplaceTextEverywhere(ctx: CanvasActionContext, input: ReplaceTextInput): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const preSummary = toCanvasSnapshotSummary(pre);
  const refuser = (code: string, message: string, suite: Pick<Parameters<typeof abortedBeforeMutation>[0], 'recovery' | 'replaceEverywhere'> = {}): CanvasActionReceipt => (
    abortedBeforeMutation({ intent: input.action, snapshot: preSummary, code, message, ...suite })
  );
  const perimee = (quand: string, revision: string): CanvasActionReceipt => refuser(
    'STALE_CANVAS_REVISION',
    `${VERBE} was refused ${quand} because the live document revision (${revision}) no longer equals the inspected revision (${ctx.expectedRevision}). Nothing was written; run canvas_inspect and resend the call with counts read from the current document.`,
  );

  const couples = couplesDeLAppel(input);
  if (couples === null) {
    return refuser(
      'REPLACE_EVERYWHERE_EXPECTED_COUNT_REQUIRED',
      `${VERBE} was refused before any read: at least one of its ${input.edits.length} edit(s) carries no expectedCount. Nothing was written; give on each edit the number of times its find occurs in the document, then resend.`,
      { recovery: { action: 'retry', reason: 'Nothing was written: add expectedCount to every edit and resend, without re-reading.' } },
    );
  }
  if (ctx.expectedRevision === undefined) {
    return refuser('EXPECTED_REVISION_REQUIRED', `${VERBE} was refused before any read because the action envelope carries no expected document revision. Nothing was written; run canvas_inspect and resend the call.`);
  }
  if (typeof ctx.doc.mutations?.preview !== 'function' || typeof ctx.doc.mutations.apply !== 'function') {
    return refuser('CAPABILITY_UNAVAILABLE', `${VERBE} requires both editor.doc.mutations.preview and editor.doc.mutations.apply. Nothing was written; replace each occurrence with replace_text and a scope instead.`);
  }
  if (pre.revision !== ctx.expectedRevision) return perimee('before reading the document file', pre.revision);

  // Premier export : le fichier où chercher. Sa révision est relue APRÈS l'export — une frappe pendant qu'il tourne fausserait les positions.
  const fichier = await relireLeFichierDeLActe(ctx.doc);
  if ('erreur' in fichier) {
    if (!(fichier.erreur instanceof ExportDeLActeImpossible || fichier.erreur instanceof LectureDeFormeImpossible)) throw fichier.erreur;
    return refuser(
      'REPLACE_EVERYWHERE_FILE_UNREADABLE',
      `${VERBE} was refused before any write: the document file could not be read to find the texts (${fichier.cause}) Nothing was written; replace each occurrence with replace_text and a scope, or apply_edit_plan, instead.`,
    );
  }
  const apresLExport = await revisionCourante(ctx, pre.revision);
  if (apresLExport !== ctx.expectedRevision) return perimee('after reading the document file', apresLExport);

  const acte = corpsAvecSesCellules(fichier.acte);
  const releve = occurrencesARemplacer(acte, couples);
  const recuDuRefus = (pairs: CanvasReplaceEverywhereReceipt['pairs']): CanvasReplaceEverywhereReceipt => ({ scope: 'body_and_tables', pairsGiven: couples.length, occurrencesWritten: 0, pairs });
  const refuserLeReleve = (refus: { code: string; message: string; pairs: CanvasReplaceEverywhereReceipt['pairs'] }): CanvasActionReceipt => refuser(refus.code, refus.message, {
    replaceEverywhere: recuDuRefus(refus.pairs),
    recovery: { action: 'retry', reason: 'Nothing was written and the inspected scope is intact: correct the call as the error message says and resend it, without re-reading.' },
  });
  const verdict = verdictDesComptes(couples, releve);
  if ('refus' in verdict) return refuserLeReleve(verdict.refus);

  const etapes = etapesDeRemplacement(verdict.ecrire);
  const mode = modeDeRevision(ctx);
  const plan = { atomic: true as const, changeMode: mode, expectedRevision: ctx.expectedRevision, steps: etapes.map(({ etape }) => etape) };

  let apercu: unknown;
  try {
    apercu = await ctx.doc.mutations.preview(plan);
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    return refuser('REPLACE_EVERYWHERE_PREVIEW_UNAVAILABLE', `${VERBE} could not complete the editor preview of its ${etapes.length}-step plan before any write (${erreur instanceof Error ? erreur.message.slice(0, 200) : 'no cause given'}). Nothing was written; run canvas_inspect once the editor is ready, then resend the call.`);
  }
  const lu = lirePreview(apercu);
  if (lu === null || lu.evaluatedRevision !== ctx.expectedRevision) {
    return refuser('REPLACE_EVERYWHERE_PREVIEW_UNAVAILABLE', `${VERBE} was refused before any write because the editor preview did not return a readable result on the expected document revision (${ctx.expectedRevision}). Nothing was written; run canvas_inspect and resend the call.`);
  }
  if (!lu.valid) {
    // Un aperçu invalide dit aussi « le document a changé » : seule la révision relue le distingue d'une étape refusée.
    const apresLApercu = await revisionCourante(ctx, pre.revision);
    if (apresLApercu !== ctx.expectedRevision) return perimee('at preview', apresLApercu);
    const refusees = (lu.failures ?? []).flatMap(({ stepId, code, message }) => {
      const enCause = etapes.find(({ etape }) => etape.id === stepId);
      return enCause === undefined ? [] : [{ occurrence: enCause.occurrence, cause: causeDeLEditeur(code, message) }];
    });
    if (refusees.length === 0) {
      return refuser('REPLACE_EVERYWHERE_PREVIEW_UNAVAILABLE', `${VERBE} was refused by the editor preview before any write, and the editor named no step of the plan. Nothing was written; run canvas_inspect and resend the call.`);
    }
    return refuserLeReleve(refusHorsDePortee(couples, releve, refusees));
  }
  if (lu.steps.length !== etapes.length || lu.steps.some((step, rang) => step.stepId !== etapes[rang]!.etape.id || step.op !== 'text.rewrite')) {
    return refuser('REPLACE_EVERYWHERE_PREVIEW_UNAVAILABLE', `${VERBE} was refused before any write because the editor preview did not return the exact ordered ${etapes.length}-step plan. Nothing was written; run canvas_inspect and resend the call.`);
  }
  const nonProuvees = etapesNonProuvees(apercu, new Map(lu.steps.map((step) => [step.stepId, step.matchCount])), etapes);
  if (nonProuvees.length > 0) return refuserLeReleve(refusHorsDePortee(couples, releve, nonProuvees));

  // Seconde clôture, après l'aperçu : l'application ne doit jamais être la première à découvrir un document qui a bougé.
  ctx.verifierEpoque('replace_text_everywhere:after-preview');
  const avantLEcriture = await revisionCourante(ctx, pre.revision);
  if (avantLEcriture !== ctx.expectedRevision) return perimee('after preview', avantLEcriture);

  let rendu: unknown;
  try {
    rendu = await muterSousFences(ctx, 'replace_text_everywhere:apply', { changeMode: mode, expectedRevision: ctx.expectedRevision }, () => ctx.doc.mutations?.apply?.(plan));
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    // Un texte qui traverse un champ Word vide fait JETER l'application, que l'aperçu disait valide. La révision inchangée prouve que rien n'est écrit.
    if (String(erreur instanceof Error ? erreur.message : erreur).includes(REFUS_TEXTE_DANS_UN_CHAMP)
      && (await revisionCourante(ctx, pre.revision)) === ctx.expectedRevision) {
      return refuser(
        'REPLACE_EVERYWHERE_TEXT_SPANS_FIELD',
        `${VERBE} wrote nothing: the editor refused the whole plan (${REFUS_TEXTE_DANS_UN_CHAMP}) because one of the texts to replace spans a Word field, and it does not name the block. `
          + `The document revision is still ${ctx.expectedRevision} and the document is NOT locked: do not tell the lawyer to finish it in Word. `
          + 'Next step: replace the texts block by block with replace_text and a scope (it removes the empty leftover fields of the block it edits before writing); the same call resent as is will be refused again.',
        { recovery: { action: 'retry', reason: 'Nothing was written. replace_text with a scope removes the empty fields that block this plan; the same everywhere call resent as is will be refused again.' } },
      );
    }
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'REPLACE_EVERYWHERE_APPLY_UNCONFIRMED',
      message: `${VERBE} was submitted but the editor did not return a receipt. The replacements may or may not have been written; do not resend the call. Run canvas_inspect and decide from the live document.`,
    });
  }

  let post;
  try {
    post = await snapshot(ctx);
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'REPLACE_EVERYWHERE_APPLY_UNCONFIRMED',
      message: `${VERBE} was submitted, but the document could not be read afterwards. The replacements may or may not have been written; do not resend the call. Run canvas_inspect and decide from the live document.`,
    });
  }
  const postSummary = toCanvasSnapshotSummary(post);
  const recu = lireRecuDePlan(rendu);
  const nonProuveesApres = recu === null
    ? []
    : etapes.flatMap(({ etape }, rang): string[] => {
      const rendue = recu.steps[rang];
      return rendue !== undefined && rendue.stepId === etape.id && rendue.op === 'text.rewrite' && rendue.effect === 'changed' && rendue.matchCount === 1 ? [] : [etape.id];
    });
  const revisionsCoherentes = recu !== null && recu.revision.before === pre.revision && recu.revision.after === post.revision;
  if (recu === null || !revisionsCoherentes || nonProuveesApres.length > 0 || recu.steps.length !== etapes.length) {
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSummary,
      postSnapshot: postSummary,
      code: 'REPLACE_EVERYWHERE_APPLY_UNCONFIRMED',
      message: `${VERBE} returned, but the editor receipt does not prove the plan against the live document`
        + (recu === null ? ' (unreadable receipt)' : nonProuveesApres.length > 0 ? ` (step(s) not proven changed exactly once: ${nonProuveesApres.slice(0, NOMMES_AU_PLUS).join(', ')})` : ' (its revisions are not those read before and after)')
        + '. The replacements may or may not have been written; do not resend the call. Run canvas_inspect and decide from the live document.',
    });
  }

  // Second export : ce que le fichier porte maintenant, clôturé sur la révision d'après (prouvée égale à celle du reçu).
  const attendus = textesAttendus(acte, verdict.ecrire);
  // L'application est prouvée : une relecture qui lève ne doit pas faire perdre ce reçu, ni laisser croire que rien n'est écrit.
  let relu: Awaited<ReturnType<typeof relireLActeApresLEcriture>> | { nonRelu: string };
  try {
    relu = await relireLActeApresLEcriture(ctx, post.revision === REVISION_ABSENTE ? undefined : post.revision);
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    relu = { nonRelu: `reread_threw: ${erreur instanceof Error ? erreur.message.slice(0, 200) : 'no cause given'}` };
  }
  let relecture: VerificationCheck;
  let nonRelu = '';
  if ('acte' in relu) {
    const apres = new Map(corpsAvecSesCellules(relu.acte).paragraphes.flatMap((paragraphe): Array<[string, string]> => (
      paragraphe.nodeId === null ? [] : [[paragraphe.nodeId, texteDuParagraphe(paragraphe)]]
    )));
    const differents = [...attendus].filter(([nodeId, attendu]) => apres.get(nodeId) !== attendu).map(([nodeId]) => nodeId);
    relecture = {
      name: 'replaced_text_reread',
      passed: differents.length === 0,
      message: differents.length === 0
        ? `The exported document file reads the expected text in each of the ${attendus.size} rewritten block(s).`
        : `The exported document file does not read the expected text in ${differents.length} of the ${attendus.size} rewritten block(s): ${differents.slice(0, NOMMES_AU_PLUS).join(', ')}${differents.length > NOMMES_AU_PLUS ? ` and ${differents.length - NOMMES_AU_PLUS} more` : ''}. Read these blocks before any other edit.`,
    };
  } else {
    nonRelu = ` The replacements ARE written, but the document file could not be re-read afterwards (${relu.nonRelu}): the proof is the editor receipt alone. Do not resend the call; read a rewritten block if you need to confirm its text.`;
    relecture = {
      name: 'replaced_text_reread',
      passed: true,
      message: `Declared, not checked: the document file could not be re-read after the write (${relu.nonRelu}), so the proof is the editor receipt alone.`,
    };
  }

  const suiviActif = mode === 'tracked';
  const blocs = [...attendus.keys()];
  const receipt = buildVerifiedReceipt({
    intent: input.action,
    preSnapshot: preSummary,
    postSnapshot: postSummary,
    // Le fichier ne donne pas l'ordinal d'un bloc, et le courtier ne lit pas les cibles résolues de ce verbe : les blocs écrits sont dans l'effet.
    resolvedTargets: [],
    provider: rendu,
    revisionsAvant: idsDeRevision(pre.trackedChanges),
    revisionsApres: idsDeRevision(post.trackedChanges),
    mouvementAttendu: suiviActif ? 'apparition' : 'aucun',
    regimeSuivi: ctx.regimeSuivi,
    verification: [
      { name: 'native_plan_receipt', passed: true, message: `The editor receipt proves each of the ${etapes.length} step(s) changed its text, found exactly once, between the revisions read before and after.` },
      relecture,
    ],
    effects: [{
      kind: 'replace_text',
      reviewability: suiviActif ? 'tracked' : 'direct_untracked',
      description: `Replaced ${etapes.length} occurrence(s) of ${couples.length} text(s) in ${blocs.length} block(s) of the body, in one atomic write in the document review mode.${nonRelu} ${PORTEE_DITE}`,
      nodeIds: blocs,
    }],
  });
  ctx.verifierEpoque('replace_text_everywhere:receipt');
  // `ok`, ou écrit sans révision suivie (`partial`) : le texte est là, le reçu compte ce qui est écrit. Tout autre statut n'en promet rien.
  return receipt.status === 'ok' || receipt.status === 'partial'
    ? { ...receipt, replaceEverywhere: { scope: 'body_and_tables', pairsGiven: couples.length, occurrencesWritten: etapes.length, pairs: [] } }
    : receipt;
}
